import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpStatusCode,
} from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { inject } from '@angular/core';
import { AuthService } from '@ieCore/services/auth.service';
import { Router } from '@angular/router';
import { NavService } from '@ieCore/services/nav.service';
import { NetworkService } from '@ieCore/services/network.service';
import { NotificationService } from '@ieCore/services/notification.service';
import { isIeCall } from '@ieCore/helpers/interceptor.helper';
import { NO_TITLE } from '@ieCore/consts/notification.consts';
import { RETURN_URL_PARAM } from '@ieCore/utils/return-url.utils';
import logger from '@ieShared/utils/logger.utils';
import {
  BYPASS_FORBIDDEN_REDIRECT,
  BYPASS_SERVER_ERROR_TOAST,
} from '@ieCore/interceptors/http-context-tokens';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isIeCall(req)) {
    return next(req);
  }

  const networkService = inject(NetworkService);
  const authService = inject(AuthService);
  const navService = inject(NavService);
  const notificationService = inject(NotificationService);
  const router = inject(Router);

  return next(req).pipe(
    tap({
      next: (res: any) => {
        if (res && res.status === 200) {
          networkService.setOnline();
        }
      },
    }),
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        switch (error.status) {
          case 0: {
            // status 0 = no HTTP response. This also covers a single backend
            // service being unreachable, a cancelled/aborted request, and CORS
            // failures — so only flag "offline" when the browser confirms it
            // (otherwise a transient/per-service failure falsely shows the
            // global "No internet connection" banner on page load).
            networkService.setOfflineIfDisconnected();
            break;
          }
          case HttpStatusCode.Unauthorized: {
            if (authService.isAuthenticated()) {
              authService.removeAuthData();
              // A 401 on an authenticated session means a stale token: the
              // guard's presence-check let the page render, so the deep link
              // the user came for IS the current URL. Carry it to login the
              // way authGuard does — captured raw, validated only on consume
              // (`safeReturnUrl()`). During initial navigation `router.url`
              // is still '/', which is nothing worth returning to — behave
              // exactly as before then. Two separate `navigate()` calls on
              // purpose: `navigate(commands, undefined)` is not the same call
              // shape as `navigate(commands)`, and the specs pin today's
              // shape by argument list — do not collapse them into one call.
              const currentUrl = router.url;
              const navigated =
                currentUrl && currentUrl !== '/'
                  ? router.navigate(navService.to.login(), {
                      queryParams: { [RETURN_URL_PARAM]: currentUrl },
                    })
                  : router.navigate(navService.to.login());
              navigated.then((e) => logger.log(e));
            }
            break;
          }
          case HttpStatusCode.Forbidden: {
            // Page-level access is enforced by route guards (→ /not-found) before
            // a page ever loads, so a 403 here means a legitimate page made a
            // request the server rejected. Surface the server's reason as a toast
            // instead of a blanket full-page redirect that hides it.
            // BYPASS_FORBIDDEN_REDIRECT = the caller owns its 403 handling
            // (fail-closed probes, the autosave inline-error UX) → stay silent.
            if (!req.context.get(BYPASS_FORBIDDEN_REDIRECT)) {
              const serverMessage =
                typeof error.error?.message === 'string' &&
                error.error.message.trim()
                  ? error.error.message
                  : 'PERMISSION_DENIED';
              notificationService.error(NO_TITLE, serverMessage);
            }
            break;
          }
          case HttpStatusCode.PreconditionFailed: {
            if (authService.isAuthenticated()) {
              router
                .navigate(navService.to.forcePasswordReset())
                .then((e) => logger.log(e));
            }
            break;
          }
          default: {
            // OWNERSHIP BOUNDARY (Wave 5, ruled 2026-08-20) — the interceptor
            // side of the split pinned by INTERCEPTOR_OWNED_STATUSES in
            // user.component.ts and asserted from both spec files:
            //   >= 500          → THIS branch: one generic toast, app-wide.
            //   0 / 401/403/412 → the dedicated cases above, unchanged.
            //   every other 4xx → the calling screen (inline field errors or
            //                     its own toast) — falling through silently
            //                     here is deliberate, not an omission.
            // BYPASS_SERVER_ERROR_TOAST = the caller owns its 5xx UX → stay
            // silent. The error is re-thrown below either way.
            if (
              error.status >= 500 &&
              !req.context.get(BYPASS_SERVER_ERROR_TOAST)
            ) {
              // Generic translated copy only: the server-authored message must
              // never render while 5xx sanitization is deferred (Bug 1's 500
              // leaked a raw Mongoose string). It goes to the debug-gated
              // logger instead.
              logger.log(
                'Unhandled server error',
                error.status,
                req.method,
                req.url,
                error.error?.message ?? error.message,
              );
              notificationService.error(NO_TITLE, 'SERVER_ERROR');
            }
            break;
          }
        }
      }
      return throwError(error);
    }),
  );
};
